<template>

	<div class="main list-view featured-products">

		<div class="sub-header">
			<div class="input-with-control">
				<input type="text" ref="search_term" v-model="filters.search_term" placeholder="Search..." @keyup.enter="setFilter()" v-focus>
				<div class="button action input-go" @click="setFilter()"><i class="fas fa-search"></i></div>
			</div>
			<div class="controls">
				<pagination :paging="paging" @updatePage="goToPage"></pagination>
			</div>
		</div>

		<div v-if="showFilters" class="filters">
			<div class="title">Filter features you have set for these page types...</div>
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.home_page ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('home_page')"></span>
					<label>Home Page</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.department_group ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('department_group')"></span>
					<label>Group</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.department ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('department')"></span>
					<label>Department</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.category ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('category')"></span>
					<label>Category</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.manufacturer ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('manufacturer')"></span>
					<label>Manufacturer</label>
				</li>
			</ul>
			<div :class="['button', hasFilters ? 'neutral' : 'disabled' ]" @click="clearFilters(true)">Clear Filters</div>
			<div v-if="hasUnavailable">
				<br>
				<div class="title">Remove any unavailable products from your featured list...</div>
				<div class="button delete" @click="showModalConfirm = true"><span>Remove Unavailable</span></div>
			</div>

			<div class="divider"></div>

			<div v-if="!autoPilotEnabled && !featureDetail" class="button primary" @click="addingProducts = true"><span>Add Featured Product</span></div>

			<div v-if="autoPilotEnabled" class="label">
				<p>These featured products are part Auto Pilot and cannot be changed.</p>
				<router-link to="/website/auto-pilot">Manage Auto Pilot...</router-link>
			</div>

			<div v-if="featureDetail">
				<button class="button neutral" @click="clearDetail()">Back to List</button>
				<button v-if="!autoPilotEnabled" class="button primary" @click="saveFeature()">Save Featured Product</button>
				<div class="message">{{ message }}</div>
			</div>
		</div>

		<div class="main-content detail" v-if="!featureDetail">
			<loading v-if="loading" class="main-content">
			    Loading featured products<br />
			</loading>
			<div v-if="features.length > 0" class="list-grid">
				<router-link v-for="feature in features" :key="feature.feature_product_id" tag="div" class="cell clickable-cell feature-cell" :to="'/website/featured-products/' + feature.product_id">
					<div style="position: relative; text-align: center; width: 100%; height: 10em; padding: 1em 3em;">
						<img v-if="feature.image_src" :src="feature.image_src" style="width: auto; max-width: 100%; max-height: 100%; position: absolute; top: 0; bottom: 0; left: 0; right: 0; margin: auto;">
						<div v-else class="no-image">No Image</div>
					</div>
					<div class="title">{{ feature.description }}</div>
					<div class="meta">
						<div style="float: left;"><span class="label">UPC:</span> {{ feature.upc || 'N/A' }}</div>
						<div style="float: right;"><span class="label">Available:</span> <span :class="{ unavailable: feature.available < 1}">{{ feature.available || '0' }}</span></div>
					</div>
				</router-link>
			</div>
			<div v-else class="empty-list">
				This list is empty
			</div>
		</div>

		<div v-if="featureDetail" class="main-content detail">
			<div class="two-columns">
				<img :src="feature.image_src">
				<div>
					<label class="full">Description</label>
					<b>{{ feature.description }}</b><br><br>
					<label class="full">Department</label>
					<b>{{ feature.department }}</b><br><br>
					<label class="full">Category</label>
					<b>{{ feature.category }}</b><br><br>
					<label class="full">Manufacturer</label>
					<b>{{ feature.manufacturer }}</b><br><br>
					<div class="two-columns">
						<label class="full">UPC</label>
						<label class="full">Available</label>
						<div><b>{{ feature.upc }}</b><br><br></div>
						<div><b>{{ feature.available }}</b><br><br></div>
						<label class="full">Cost</label>
						<label class="full">Price</label>
						<div><b>{{ $formatCurrency(feature.cost) }}</b></div>
						<div><b>{{ $formatCurrency(feature.price) }}</b></div>
					</div>
					<div style="margin: 1em 0 0 0;">
						<div class="button small neutral" @click="setOverride(feature)">Price Override</div>
					</div>
				</div>
			</div>

			<div class="add-remove">
				<div>
					<div class="heading">Include this featured product on a page type</div>
					<select v-model="add_to" @change="setAddTo()" :disabled="autoPilotEnabled">
						<option value="">Select...</option>
						<option v-for="(definition,index) in listDefinitions" :value="index">{{ definition.name }}</option>
					</select>
				</div>
				<div>
					<div class="heading">Page types selected</div>
					<table class="items-selected">
						<tr v-for="(feature_page, index) in feature_pages">
							<td><button :class="autoPilotEnabled ? 'disabled' : 'delete'" @click="removeFeaturePage(index)"><i class="fas fa-times link pointer"></i></button></td>
							<td>{{ getPageTypeDescription(feature_page) }}</td>
							<td></td>
						</tr>
					</table>
				</div>
			</div>

		</div>

		<div class="modal" v-if="showModalConfirm">
			<div class="inner">
				<h2>Confirm Remove Unavailable</h2>
				<div class="content">
					Are you sure you want to remove {{ hasUnavailable }} products from your featured product set?
				</div>
				<div class="controls">
					<div class="button cancel" @click="showModalConfirm = false">Cancel</div>
					<div class="button action" @click="removeUnavailable()">Yes Please</div>
				</div>
			</div>
		</div>

		<price-override-detail v-if="showPriceOverride" :listDetail="priceOverride" @backToList="finishedPriceOverride" />

		<product-search v-if="addingProducts" singleProduct="true" searchFor="Featured Products" @assignProduct="assignProductFromSearch" @cancel="addingProducts = false" />

		<help-popup :helpLink="{82:'Featured Products', 107:'Website Template: Featured Products Section'}" :tours="false"></help-popup>

	</div>

</template>

<script>
import ProductSearch from '@/components/ProductSearch.vue'
import PriceOverrideDetail from '@/components/PriceOverrideDetail.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: '',
	props: ['feature_product_id'],
    components: {
		'product-search': ProductSearch,
		'price-override-detail': PriceOverrideDetail,
		'help-popup': HelpPopup
    },
	data() {
		return {
			loading: false,
			showFilters: true,
			filters: {
				page: 1,
				search_term: '',
				home_page: false,
				department_group: false,
				department: false,
				category: false,
				manufacturer: false
			},
			paging: {},
			message: '',
			clientId: '',
			features: [],
			featureDetail: '',
			feature: {},
			feature_pages: [],
			add_to: '',
			listDefinitions: {},
			featureSaved: false,
			showModalConfirm: false,
			autoPilotEnabled: false,
			addingProducts: false,
			priceOverride: {},
			showPriceOverride: false
		}
	},
	created() {
		this.getAutoPilotEnabled()
		this.getList()
		if (this.feature_product_id) {
	    	this.featureDetail = this.feature_product_id
			this.getDetail()
    	}
	},
	watch: {
		feature_product_id: function(to, from) {
	    	this.featureDetail = this.feature_product_id
	    	if (this.featureDetail) {
				this.getDetail()
			}
		}
	},
	computed: {
		hasFilters() {
			let filterIsSet = false
			for (var property in this.filters) {
				switch (property) {
					case 'hostname':
					case 'page':
					case 'source':
					case 'shopping_cart_id':
						break
					default:
						filterIsSet = this.filters[property] && this.filters[property] != ''
						break
				}
				if (filterIsSet) {
					return true
				}
			}
			return false
		},
		hasUnavailable() {
			let unavailableCount = 0
			for (var i = 0; i < this.features.length; i++) {
				if (this.features[i].available == 0) {
					unavailableCount++
				}
			}
			return unavailableCount
		}
	},
	methods: {
		getAutoPilotEnabled() {
			this.apiCall({ destination: 'get_item_preference', data: { preference_code: 'ENABLE_AUTO_PILOT' } }).then(obj => {
				this.autoPilotEnabled = obj.ENABLE_AUTO_PILOT != ''
			}).catch(err => {
				console.log(err)
			})
		},
		getList() {
			this.featureDetail = ''
			this.loading = true
			this.apiCall({ destination: 'get_list_feature_products', data: this.filters }).then(obj => {
				this.loading = false
				if (obj.features) {
					this.features = obj.features
					this.paging = obj.paging
					this.clientId = obj.client_id
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setFilter() {
			this.filters.page = 1
			this.getList()
		},
		clearFilters(getList) {
			this.filters = {
				page: 1,
				search_term: '',
				home_page: false,
				department_group: false,
				department: false,
				category: false,
				manufacturer: false
			}
			if (getList) {
				this.getList()
			}
		},
		toggleFilter(which) {
			let currentValue = this.filters[which]
			this.filters.home_page = false
			this.filters.department_group = false
			this.filters.department = false
			this.filters.category = false
			this.filters.manufacturer = false
			this.filters[which] = !currentValue
			this.filters.page = 1
			this.getList()
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		// searchProducts() {
		// 	if (!this.productSearchTerm) {
		// 		return false
		// 	}
		// 	this.productList = []
		// 	this.productSearchMessage = 'Searching...'
		// 	this.apiCall({ destination: 'get_list_products', data: { search_term: this.productSearchTerm } }).then(obj => {
		// 		if (obj.list && obj.list.products) {
		// 			if (obj.list.products.length == 1) {
		// 				this.showModal = false
		// 				this.productSearchTerm = ''
		// 				this.productSearchMessage = ''
		// 				this.featureDetail = obj.list.products[0]['product_id']
		// 				this.getDetail()
		// 			} else if (obj.list.products.length > 1) {
		// 				this.productSearchMessage = ''
		// 				this.productList = obj.list.products
		// 			} else {
		// 				this.productSearchMessage = 'No matching products found.'
		// 			}
		// 		}
		// 	}).catch(err => {
		// 		console.log(err)
		// 	})
		// },
		assignProductFromSearch(product) {
			//this.showModal = false
			//this.productSearchTerm = ''
			//this.productList = []
			this.addingProducts = false
			this.featureDetail = product.product_id
			this.getDetail()
		},
		setOverride(product) {
			this.priceOverride = product
			this.showPriceOverride = true
		},
		finishedPriceOverride(updateRequired) {
			if (updateRequired) {
				this.getDetail()
			}
			this.showPriceOverride = false
		},
		getDetail() {
			this.message = ''
			this.feature = {}
			this.feature_pages = []
			this.listDefinitions = {}
			if (this.feature_product_id == 'new') {
				this.feature.client_id = this.clientId
			} else {
				this.loading = true
				this.apiCall({ destination: 'get_item_feature_product', data: { product_id: this.featureDetail } }).then(obj => {
					this.loading = false
					if (obj.feature) {
						this.feature = obj.feature
						this.feature_pages = obj.feature_pages
						this.listDefinitions = obj.definitions
					}
				}).catch(err => {
                    this.loading = false
					console.log(err)
				})
			}
		},
		clearDetail() {
			this.featureDetail = ''
			this.$router.push('/website/featured-products')
			if (this.featureSaved) {
				this.getList()
				this.featureSaved = false
			}
		},
		getPageTypeDescription(pageType) {
			let description = pageType['description']
			switch (pageType['feature_type_code']) {
				case 'DEPARTMENT':
					description += ' (' + this.feature.department + ')'
					break
				case 'CATEGORY':
					description += ' (' + this.feature.category + ')'
					break
				case 'BRAND':
					description += ' (' + this.feature.manufacturer + ')'
					break
			}
			return description
		},
		setAddTo() {
			if (this.autoPilotEnabled) { return }
			// is it already in the selected list?
			let featureTypeCode = this.listDefinitions[this.add_to]['featureTypeCode']
			let inList = false
			for (var i = 0; i < this.feature_pages.length; i++) {
				if (this.feature_pages[i]['feature_type_code'] == featureTypeCode) {
					inList = true
				}
			}
			if (!inList) {
				this.feature_pages.push({feature_page_id: '', feature_type_code: featureTypeCode, description: this.listDefinitions[this.add_to]['name']})
			}
			this.add_to = ''
		},
		removeFeaturePage(index) {
			if (this.autoPilotEnabled) { return }
			this.feature_pages.splice(index, 1)
		},
		saveFeature() {
			this.loading = true
			this.apiCall({ destination: 'save_item_feature_product', data: { product_id: this.featureDetail, feature_pages: this.feature_pages } }).then(obj => {
				this.loading = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.featureSaved = true
					if (this.feature_product_id == 'new') {
						this.$router.replace('/website/featured-products/' + obj.feature_product_id)
					} else {
						window.setTimeout(() => this.getDetail(), 1000);
					}
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeUnavailable() {
			let productIds = []
			for (var i = 0; i < this.features.length; i++) {
				if (this.features[i].available == 0) {
					productIds.push(this.features[i].product_id)
				}
			}
			this.showModalConfirm = false
			this.loading = true
			this.apiCall({ destination: 'remove_item_feature_products', data: { product_ids: productIds } }).then(obj => {
				this.loading = false
				if (obj.status == 'success') {
					this.getList()
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})

		}
	}
}
</script>

<style lang="scss" scoped>
.feature-cell {
	display: grid;
	grid-template-rows: 160px 2.5em 1em;
	align-items: center;
}
</style>
